import React, { useEffect, useState } from 'react';
import isNil from 'lodash/isNil';
import ModalDialog from '../../../../shared/components/modal/ModalDialog';
import { ModalButtonLine } from '../../../../shared/components/modal/ModalButtonLine';
import { LocalContentRadioButtonGroup } from './LocalContentRadioButtonGroup';
import { CountriesLimitedOptions } from './CountriesLimitedOptions';
import { LimitedSharingOption } from './LimitedSharingOption';
import { Button, ButtonSize, ButtonTheme } from '../../../../shared/common/Button';
import { CountryOut } from '../../../../store/apiLibx';
import { useTranslation } from 'react-i18next';

export const RESTRICTION_LEVEL_LIMITED = 'LIMITED';
export const RESTRICTION_LEVEL_WORLD = 'WORLD';

const YES = 'YES';
const NO = 'NO';

interface Props {
    defaultCountryCode?: string;
    countries: CountryOut[];
    show: boolean;
    onFinish: (sharingOptions: any) => void;
    onAbort: () => void;
}
export const SetLiveOnLibXChangePopup = (props: Props) => {
    const [share, setShare] = useState<boolean | null>(null);
    const [restriction, setRestriction] = useState<string | undefined>(undefined);
    const [countries, setCountries] = useState<string[]>([]);

    const { t } = useTranslation();

    useEffect(() => {
        if (props.countries && props.countries.length > 0 && countries?.length === 0) {
            setCountries(props.countries.map(c => c.code ?? ''));
        } else if (countries?.length === 0 && props.defaultCountryCode) {
            setCountries([props.defaultCountryCode]);
        }
    }, [countries?.length, props.countries, props.defaultCountryCode]);

    useEffect(() => {
        return () => {
            setShare(null);
            setRestriction(undefined);
            setCountries([]);
        };
    }, []);

    function onChangeLibXChange(sharing: string) {
        setCountries([]);
        setRestriction(undefined);
        setShare(sharing === YES);
    }

    function onChangeSharingRestriction(restriction: string) {
        setRestriction(restriction);
        setCountries(props.countries.map(c => c.code ?? ''));
    }

    function onSelectAllCountries() {
        setCountries(props.countries.map(c => c.code ?? ''));
    }

    function onChangeSelectedCountry(countryCode) {
        let countries: string[] = [];
        if (countries.indexOf(countryCode) >= 0) {
            countries = countries.filter(c => c !== countryCode);
        } else {
            countries = countries.slice().concat(countryCode);
        }
        setCountries(countries);
    }

    function onDeselectAllCountries() {
        setCountries([props.defaultCountryCode ?? '']);
    }

    function onFinish() {
        let sharingOptions: { shareWorldwide: boolean; countryCodes?: string[] } | undefined = undefined;
        if (share) {
            sharingOptions = {
                shareWorldwide: restriction === RESTRICTION_LEVEL_WORLD || countries?.length === props.countries.length
            };

            if (!sharingOptions?.shareWorldwide) {
                sharingOptions.countryCodes = countries;
            }
        }
        props.onFinish(sharingOptions);
    }

    return (
        <ModalDialog
            show={props.show}
            title="&nbsp;"
            titleClassName="display-none"
            onCloseButtonClick={props.onAbort}
            wrapClassName="lc-settings-popup"
            preventClosingAction={true}
        >
            <span>{t('yourLocalContent.yourTitles.step3')}</span>
            <h2>{t('yourLocalContent.yourTitles.setLiveInLibx')}</h2>
            <p style={{ fontSize: '15px' }}>{t('yourLocalContent.yourTitles.availabilityText')}</p>

            <LocalContentRadioButtonGroup
                onRadioChanged={onChangeLibXChange}
                selectedOption={share === null ? null : share ? YES : NO}
                options={[
                    { key: YES, value: t('yourLocalContent.yourTitles.yes') },
                    { key: NO, value: t('yourLocalContent.yourTitles.no') }
                ]}
            />

            {share ? (
                <LimitedSharingOption
                    onChangeSharingRestriction={onChangeSharingRestriction}
                    selectedRestriction={restriction}
                />
            ) : null}
            {!share || restriction !== RESTRICTION_LEVEL_LIMITED ? null : (
                <CountriesLimitedOptions
                    countries={props.countries}
                    selectedCountries={countries || []}
                    defaultCountryCode={props.defaultCountryCode ?? ''}
                    onToggleCountry={onChangeSelectedCountry}
                    onSelectAllCountries={onSelectAllCountries}
                    onDeselectAllCountries={onDeselectAllCountries}
                />
            )}
            <ModalButtonLine>
                <Button
                    size={ButtonSize.LARGE}
                    theme={ButtonTheme.NEUTRAL}
                    onClick={props.onAbort}
                    text={t('cancel')}
                    className="bt2 modal-button"
                />

                <Button
                    size={ButtonSize.LARGE}
                    text={t('yourLocalContent.yourTitles.finish')}
                    disabled={!(share === false || !isNil(restriction))}
                    onClick={onFinish}
                    className="bt2 modal-button"
                />
            </ModalButtonLine>
        </ModalDialog>
    );
};
